/**
 * Toast notification system with accessibility
 * - Uses token-based colors (no hardcoded blue/red)
 * - aria-live for screen reader announcements
 * - Keyboard dismiss (Escape) + close button
 */

import {
  useState,
  useCallback,
  useEffect,
  type ReactNode,
  type KeyboardEvent,
} from 'react';
import { ToastContext } from './ToastContext';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { id, type, message }]);

    // Auto-remove after 4 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Global Escape key listener to dismiss all toasts
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && toasts.length > 0) {
        setToasts([]);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toasts.length]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

/**
 * Token-based toast styling:
 * - success: primary (sky blue) with checkmark
 * - error: warning (amber) with exclamation - not color-only (has icon + text)
 * - info: secondary (violet) with info icon
 */
function getToastStyles(type: ToastType): {
  bg: string;
  text: string;
  icon: string;
  role: 'alert' | 'status';
  ariaLive: 'assertive' | 'polite';
} {
  switch (type) {
    case 'success':
      return {
        bg: 'bg-primary',
        text: 'text-white',
        icon: '✓',
        role: 'status',
        ariaLive: 'polite',
      };
    case 'error':
      return {
        bg: 'bg-warning',
        text: 'text-slate-900',
        icon: '⚠',
        role: 'alert',
        ariaLive: 'assertive',
      };
    case 'info':
      return {
        bg: 'bg-secondary',
        text: 'text-white',
        icon: 'ℹ',
        role: 'status',
        ariaLive: 'polite',
      };
  }
}

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const styles = getToastStyles(toast.type);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRemove(toast.id);
    }
  };

  return (
    <div
      className={`${styles.bg} ${styles.text} px-4 py-3 rounded-md shadow-md flex items-center gap-3 min-w-[280px] max-w-md animate-in slide-in-from-right`}
      role={styles.role}
      aria-live={styles.ariaLive}
      aria-atomic="true"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <span className="text-lg flex-shrink-0" aria-hidden="true">
        {styles.icon}
      </span>
      <p className="flex-1 text-sm">
        {toast.type === 'error' && <span className="font-medium">Error: </span>}
        {toast.message}
      </p>
      <button
        onClick={() => onRemove(toast.id)}
        className={`${styles.text} opacity-80 hover:opacity-100 transition-opacity`}
        aria-label="Dismiss notification"
      >
        ×
      </button>
    </div>
  );
}
